import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la pagina principală
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Archivo']">
                Politica de Confidențialitate
              </h1>
              <p className="text-slate-500">Ultima actualizare: Martie 2026</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <h2>1. Introducere</h2>
            <p>
              AUTO CORA ("noi", "nouă" sau "al nostru") respectă confidențialitatea vizitatorilor 
              și utilizatorilor platformei noastre. Această Politică de Confidențialitate explică 
              cum colectăm, utilizăm, stocăm și protejăm informațiile dumneavoastră personale în 
              conformitate cu Regulamentul General privind Protecția Datelor (GDPR) și legislația 
              română în vigoare.
            </p>

            <h2>2. Operatorul de Date</h2>
            <p>
              Operatorul de date personale este AUTO CORA, cu sediul în România. Pentru orice 
              întrebări legate de prelucrarea datelor personale, ne puteți contacta la adresa 
              de email disponibilă pe pagina de contact.
            </p>

            <h2>3. Datele Personale Colectate</h2>
            <p>Colectăm următoarele categorii de date personale:</p>
            <ul>
              <li><strong>Date de identificare:</strong> nume, prenume, adresă de email</li>
              <li><strong>Date de cont:</strong> informații de autentificare (parolă criptată)</li>
              <li><strong>Date de utilizare:</strong> progresul în cursuri, rezultate teste, preferințe</li>
              <li><strong>Date tehnice:</strong> adresă IP, tip browser, dispozitiv utilizat</li>
              <li><strong>Date de plată:</strong> procesate securizat prin furnizorul nostru de plăți (Stripe)</li>
            </ul>

            <h2>4. Scopul Prelucrării</h2>
            <p>Utilizăm datele dumneavoastră personale pentru:</p>
            <ul>
              <li>Furnizarea serviciilor educaționale și administrarea contului</li>
              <li>Procesarea plăților și gestionarea abonamentelor</li>
              <li>Comunicări legate de serviciile noastre</li>
              <li>Îmbunătățirea platformei și experienței utilizatorului</li>
              <li>Respectarea obligațiilor legale</li>
            </ul>

            <h2>5. Temeiul Legal al Prelucrării</h2>
            <p>Prelucrăm datele dumneavoastră pe baza:</p>
            <ul>
              <li><strong>Executarea contractului:</strong> pentru furnizarea serviciilor solicitate</li>
              <li><strong>Consimțământul:</strong> pentru comunicări de marketing (dacă ați optat)</li>
              <li><strong>Interesul legitim:</strong> pentru îmbunătățirea serviciilor</li>
              <li><strong>Obligații legale:</strong> pentru conformitate fiscală și legală</li>
            </ul>

            <h2>6. Partajarea Datelor</h2>
            <p>
              Nu vindem datele dumneavoastră personale. Putem partaja date cu:
            </p>
            <ul>
              <li><strong>Stripe:</strong> pentru procesarea securizată a plăților</li>
              <li><strong>Furnizori de hosting:</strong> pentru stocarea securizată a datelor</li>
              <li><strong>Autorități:</strong> când este cerut de lege</li>
            </ul>

            <h2>7. Durata Stocării</h2>
            <p>
              Păstrăm datele dumneavoastră atât timp cât aveți un cont activ sau cât este 
              necesar pentru furnizarea serviciilor. Datele financiare sunt păstrate conform 
              cerințelor legale (de regulă, 10 ani).
            </p>

            <h2>8. Drepturile Dumneavoastră</h2>
            <p>Conform GDPR, aveți următoarele drepturi:</p>
            <ul>
              <li><strong>Dreptul de acces:</strong> să solicitați o copie a datelor dumneavoastră</li>
              <li><strong>Dreptul la rectificare:</strong> să corectați datele inexacte</li>
              <li><strong>Dreptul la ștergere:</strong> să solicitați ștergerea datelor ("dreptul de a fi uitat")</li>
              <li><strong>Dreptul la restricționare:</strong> să limitați prelucrarea datelor</li>
              <li><strong>Dreptul la portabilitate:</strong> să primiți datele într-un format structurat</li>
              <li><strong>Dreptul de opoziție:</strong> să vă opuneți prelucrării în anumite situații</li>
            </ul>
            <p>
              Pentru exercitarea acestor drepturi, contactați-ne prin pagina de Contact.
            </p>

            <h2>9. Securitatea Datelor</h2>
            <p>
              Implementăm măsuri tehnice și organizatorice adecvate pentru protejarea datelor, 
              inclusiv criptare, acces restricționat și monitorizare continuă.
            </p>

            <h2>10. Cookie-uri</h2>
            <p>
              Utilizăm cookie-uri pentru funcționarea platformei. Pentru detalii complete, 
              consultați <Link to="/politica-cookies" className="text-blue-600 hover:underline">Politica de Cookies</Link>.
            </p>

            <h2>11. Modificări ale Politicii</h2>
            <p>
              Ne rezervăm dreptul de a actualiza această politică. Modificările semnificative 
              vor fi comunicate prin email sau pe platformă.
            </p>

            <h2>12. Contact</h2>
            <p>
              Pentru întrebări sau solicitări privind datele personale, ne puteți contacta 
              prin formularul disponibil pe pagina de <Link to="/contact" className="text-blue-600 hover:underline">Contact</Link>.
            </p>
            <p>
              De asemenea, aveți dreptul de a depune o plângere la Autoritatea Națională de 
              Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
